/**
 * Importing the recorded sort run from the demo prefix (docs/ux/intake.md).
 *
 * The casefile tab's empty state used to describe `upload-sort-run`, a command
 * line tool, to a reader looking at a screen. This puts the run on the case
 * from the same place, where the demo tools are on; where they are off the
 * control is absent and the description alone stands, which is the honest
 * state for a build that has no demo files.
 *
 * Nothing here re-sorts anything. A live sort is the sorter's work.
 */

import { useState } from 'react';
import { toast } from 'sonner';
import { reasonOf } from '@/app/api';
import { Button } from '@/components/ui/button';
import { useDemoPrefill, useSortFromDemo } from './useIntake';

export function ImportDemoRun({ matterRef }: { matterRef: string }) {
  const [error, setError] = useState<string | null>(null);
  const prefill = useDemoPrefill();
  const importRun = useSortFromDemo(matterRef);
  if (!prefill.data) return null;

  async function run() {
    setError(null);
    try {
      // The run id is the case's own reference: one recorded run per case is all the demo needs.
      await importRun.mutateAsync(`demo-${matterRef.toLowerCase()}`);
      toast.success('Importing the recorded run. The replay appears when the job finishes.');
    } catch (err) {
      setError(reasonOf(err) ?? 'The demo prefix holds no run to import.');
    }
  }

  return (
    <div className="flex flex-col items-center gap-2" data-slot="import-demo-run">
      <Button onClick={() => void run()} disabled={importRun.isPending}>
        {importRun.isPending ? 'Importing' : 'Import the demo run'}
      </Button>
      {error ? <p role="alert" className="text-[13px] text-beam-error">{error}</p> : null}
    </div>
  );
}
